import React from 'react';
import { get } from 'lodash-es';
import { CmsModel } from 'client/data/models/cms';
import { HomeModulesModel } from 'client/data/models/home-modules';
import { getMeta } from 'site-modules/shared/utils/seo-helper';
import { getModelState } from 'client/data/luckdragon/model';
import { parseContent } from 'client/data/cms/content';
import { pageDefinition } from 'client/site-modules/shared/pages/page';
import { logger } from 'client/utils/logger/logger';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { InventoryModel } from 'client/data/models/inventory';
import { VisitorModel } from 'client/data/models/visitor';
import { PageModel } from 'client/data/models/page';
import { buildClosestIcoDealersPath, DealerIcoWidgetModel } from 'client/data/models/dealer-ico-widget';
import { getQuery } from 'client/utils/location';
import { getBreadcrumbSchema } from 'client/seo/schemas/breadcrumbs';
import { AFFORDABILITY_BREADCRUMBS } from 'site-modules/calculators/constants/calculators';
import {
  getCalculatorPaymentValues,
  calculatePayments,
  getSearchFilterParamsWithDealerId,
  getIsCarmaxTraffic,
} from 'site-modules/calculators/utils/affordability-calculator-utils';
import { getCalculatorUrlParams } from 'site-modules/calculators/utils/get-calculator-url-params';
import { getFAQSchema } from 'client/seo/schemas/faq';
import { getQAEntriesFromContent } from 'client/site-modules/shared/utils/faq';
import { setCriticalCss } from 'client/actions/critical-css';
import { CalculatorsAffordability, CALCULATORS_AFFORDABILITY_CONTENT_FILE_PATH } from './calculators-affordability';

export const pageDefinitionConfiguration = {
  name: 'calculators_affordability',
  category: 'calculators_affordability',
  preload: async (store, props) => {
    const preloader = new ModelPreloader(store);
    preloader.resolve('location', VisitorModel);
    preloader.resolve(`content["${CALCULATORS_AFFORDABILITY_CONTENT_FILE_PATH}"]`, CmsModel);
    preloader.resolve('homeHeroContent', HomeModulesModel);

    let modelState = await preloader.load();

    const salesTax = modelState.get('location.salesTax', VisitorModel);
    const location = modelState.get('location', PageModel);
    const isCarmaxTraffic = getIsCarmaxTraffic(location);
    const { price } = getCalculatorUrlParams(props);
    const { stickerPriceMin, stickerPriceMax } = calculatePayments({
      payments: getCalculatorPaymentValues({ location, monthlyPayment: price }),
      salesTax,
      isCarmaxTraffic,
    });

    let carMaxDealerId;
    if (isCarmaxTraffic) {
      const { zipcode: queryZipCode } = getQuery(location);
      const zipFromStore = modelState.get('location.zipCode', VisitorModel);
      const zipCode = queryZipCode || zipFromStore;
      const closestIcoDealerPath = zipCode ? buildClosestIcoDealersPath({ zipCode }) : null;
      preloader.resolve(closestIcoDealerPath, DealerIcoWidgetModel);

      modelState = await preloader.load();

      const closestIcoMultiDealers = modelState.get(closestIcoDealerPath, DealerIcoWidgetModel);
      carMaxDealerId = closestIcoMultiDealers?.carMaxDealerId;
    }

    preloader.set(
      'searchFilter',
      InventoryModel,
      getSearchFilterParamsWithDealerId(stickerPriceMin, stickerPriceMax, carMaxDealerId)
    );
    preloader.resolve('vins', InventoryModel);

    store.dispatch(
      setCriticalCss({
        isCarmaxTraffic, // 2 values
      })
    );

    try {
      await preloader.load();
    } catch (e) {
      logger.error(e);
    }
  },
  component: props => <CalculatorsAffordability {...props} {...getCalculatorUrlParams(props)} />,
  seo: (state, props) => {
    const modelState = getModelState(state);
    const calculatorContent = modelState.get(`content["${CALCULATORS_AFFORDABILITY_CONTENT_FILE_PATH}"]`, CmsModel);
    const content = parseContent(calculatorContent);
    const meta = getMeta(content);
    const faqContent = content.child('faq');
    return {
      ...meta,
      jsonld: [
        getBreadcrumbSchema(AFFORDABILITY_BREADCRUMBS),
        getFAQSchema({
          entries: getQAEntriesFromContent(faqContent),
          pathname: get(props, 'location.pathname', ''),
          generateLearnMoreLinks: true,
        }),
      ],
    };
  },
};

export default pageDefinition(pageDefinitionConfiguration);
